export type BBNode =
  | { type: "text"; text: string }
  | { type: "tag"; tag: string; attr?: string; children: BBNode[] };

const SUPPORTED_TAGS = new Set(["b", "i", "color", "size", "shake", "wave"]);

export function parseBBCode(input: string): BBNode[] {
  let pos = 0;

  function parseNodes(stopTag?: string): BBNode[] {
    const result: BBNode[] = [];
    let textStart = pos;

    while (pos < input.length) {
      if (input[pos] === "[") {
        if (pos > textStart) {
          result.push({ type: "text", text: input.slice(textStart, pos) });
        }

        const closing = parseClosingTag();
        if (closing !== null) {
          if (closing === stopTag) {
            return result;
          }
          result.push({ type: "text", text: input.slice(pos - closing.length - 3, pos) });
          textStart = pos;
          continue;
        }

        const open = parseOpenTag();
        if (open !== null) {
          const children = parseNodes(open.tag);
          const node: BBNode = { type: "tag", tag: open.tag, children };
          if (open.attr !== undefined) node.attr = open.attr;
          result.push(node);
          textStart = pos;
          continue;
        }

        result.push({ type: "text", text: "[" });
        pos++;
        textStart = pos;
        continue;
      }
      pos++;
    }

    if (pos > textStart) {
      result.push({ type: "text", text: input.slice(textStart, pos) });
    }
    return result;
  }

  function parseOpenTag(): { tag: string; attr?: string } | null {
    const saved = pos;
    if (input[pos] !== "[") { return null; }

    const end = input.indexOf("]", pos);
    if (end === -1) { pos = saved; return null; }

    const inner = input.slice(pos + 1, end);
    if (inner.startsWith("/")) { pos = saved; return null; }

    let tag: string;
    let attr: string | undefined;
    const eqIdx = inner.indexOf("=");
    if (eqIdx !== -1) {
      tag = inner.slice(0, eqIdx).toLowerCase().trim();
      attr = inner.slice(eqIdx + 1).trim();
    } else {
      tag = inner.toLowerCase().trim();
    }

    if (!SUPPORTED_TAGS.has(tag)) { pos = saved; return null; }

    pos = end + 1;
    return attr !== undefined ? { tag, attr } : { tag };
  }

  function parseClosingTag(): string | null {
    const saved = pos;
    if (input[pos] !== "[" || input[pos + 1] !== "/") { return null; }

    const end = input.indexOf("]", pos);
    if (end === -1) { pos = saved; return null; }

    const tag = input.slice(pos + 2, end).toLowerCase().trim();
    if (!SUPPORTED_TAGS.has(tag)) { pos = saved; return null; }

    pos = end + 1;
    return tag;
  }

  return parseNodes();
}

export function countVisibleChars(nodes: BBNode[]): number {
  let count = 0;
  for (const node of nodes) {
    if (node.type === "text") {
      count += node.text.length;
    } else {
      count += countVisibleChars(node.children);
    }
  }
  return count;
}
